import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { slice1Actions } from "../../../store/reducer";
import HTMLEditorModal from "../../modals/HTMLEditorModal";
import Common from "../../../Common/Common";
import GuideEditModal from "../../modals/GuideEditModal";
import "./GuideNameBar.css";

const GuideNameBar = () => {
  const dispatch = useDispatch();

  const guide_meta = useSelector((state) => state.guide_meta);
  const GuideFormat = useSelector((state) => state.GuideFormat);
  const GuideSettings = useSelector((state) => state.GuideSettings);
  const user_info = useSelector((state) => state.user_info);
  const imageFolder = useSelector((state) => state.imageFolder);
  const HTMLStack = useSelector((state) => state.HTMLStack);
  const homeScreen = useSelector((state) => state.homeScreen);
  const ShowHiddenPlaces = useSelector((state) => state.ShowHiddenPlaces);
  const HideSchedule = useSelector((state) => state.HideSchedule);
  const MobileMode = useSelector((state) => state.MobileMode);
  const GuideEditMode = useSelector((state) => state.GuideEditMode);

  const [ShowHTMLEditorModal, setShowHTMLEditorModal] = useState(false);
  const [HTMLEditorModalContent, setHTMLEditorModalContent] = useState("");
  const [GuideEditModalShow, setGuideEditModalShow] = useState(false);
  const [GuideEditModalMode, setGuideEditModalMode] = useState("UPDATE");
  // The Edit button offers a choice instead of going straight to the HTML
  // editor: new users tapped it hoping to change the guide's photo/name and
  // landed in raw HTML. menuPos is fixed-position (from the button's rect) so
  // the menu isn't clipped by the panel's overflow.
  const [EditMenuPos, setEditMenuPos] = useState(null);
  const openEditMenu = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setEditMenuPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };
  const closeEditMenu = () => setEditMenuPos(null);

  // Opens the schedule header's "Copy to Trip Itinerary" dialog (it owns that
  // flow). In mobile mode that header only exists on the Schedule screen, so
  // go there first; it returns to Home itself once the copy is done.
  const openCopyItinerary = () => {
    const fire = () => window.dispatchEvent(new CustomEvent("andiamo-open-itinerary-copy"));
    if (MobileMode) {
      dispatch(slice1Actions.setCurrentAppScreen({ value: "Schedule" }));
      setTimeout(fire, 400);
    } else fire();
  };
  const scheduleVisible = GuideSettings?.hide_schedule !== true && HideSchedule !== true;
  const hasEvents = (window.guide_obj?.schedule?.events || []).length > 0;
  const doc = window.guide_obj?.document;
  const hasItinerary = !!doc && doc !== "<!-- Comment -->";
  const openDialog = (name) => window.dispatchEvent(new CustomEvent("openAppDialog", { detail: { name } }));
  // All Build shortcuts are always listed (hiding them by guide state left
  // users unsure whether a feature existed); only Create/Update Itinerary can
  // be unavailable, and then it says why instead of vanishing.
  const isMosaic = GuideFormat === "Photo Mosaic";
  // In Photo Mosaic mode the page is generated, not hand-edited, so the two
  // page-editing items wait for User Content mode (the toggle beside Edit).
  const mosaicReason = "Switch to User Content to edit this page";
  const reloadDisabledReason = !guide_meta?.guide_info_id ? "Save the guide to the cloud first" : "";
  const itineraryDisabledReason = isMosaic
    ? mosaicReason
    : !hasEvents
    ? "Add events to your schedule first"
    : !scheduleVisible
    ? "Show the schedule pane first"
    : "";
  const MOSAIC_ITINERARY = { directions: false, info: false, photo: true, place_menu: false };

  const setImageFolder = (folder) => {
    dispatch(slice1Actions.setImageFolder({ value: folder }));
  };

  const HTMLEdit = () => {
    let html = window.guide_obj.document;
    if (html === null || html === "" || html === "<!-- Comment -->") {
      html = window.guide_obj.createGuideHTML();
    }
    setHTMLEditorModalContent(html);
    setShowHTMLEditorModal(true);
  };

  const HTMLEditCallback = (html) => {
    if (html === null || typeof html === "undefined") return;
    window.guide_obj.document = html;
    dispatch(slice1Actions.clearHTMLStack());
    dispatch(slice1Actions.setHomeScreen({ value: html }));
  };

  const EditGuideInfo = () => {
    setGuideEditModalMode("UPDATE");
    setGuideEditModalShow(true);
  };

  // The "Cover image unavailable. Tap to change it." tile in the itinerary
  // asks for the guide info editor (where the cover image is set).
  useEffect(() => {
    const open = () => EditGuideInfo();
    window.addEventListener("openGuideInfoEditor", open);
    return () => window.removeEventListener("openGuideInfoEditor", open);
  }, []);

  const GuideEditModalCallback = (guide_attributes, edit_mode) => {
    if (edit_mode === true) {
      setGuideEditModalMode("UPDATE");
      setGuideEditModalShow(true);
      return;
    }
    if (GuideEditModalMode === "UPDATE") {
      dispatch(slice1Actions.setGuide({ guide: null, update_guide_attributes: true }));
      // If the guide's image changed, carry it into both the persisted
      // document AND whatever's actually being displayed right now — those
      // can genuinely differ (e.g. ScheduleHeader.js's "Choose Schedule"
      // shows a per-schedule computed preview in state.homeScreen without
      // ever touching window.guide_obj.document). This used to always
      // redisplay window.guide_obj.document itself, which silently
      // discarded that preview and reverted the Itinerary view back to
      // whatever the persisted document represents (e.g. "Main"'s content)
      // the moment the cover image was fixed. Found 2026-09-27.
      const attrs = window.guide_obj?.attributes;
      const raw = guide_attributes?.image_m || guide_attributes?.image || attrs?.image_m || attrs?.image;
      const imgUrl = raw ? Common.getImageURL(raw, null, true) : null;
      const doc = window.guide_obj?.document;
      if (imgUrl && doc) {
        const updatedDoc = Common.replaceCoverImageURL(doc, imgUrl);
        if (updatedDoc !== doc) window.guide_obj.document = updatedDoc;
      }
      if (imgUrl && GuideFormat !== "Photo Mosaic") {
        const updatedDisplay = Common.replaceCoverImageURL(homeScreen, imgUrl);
        if (updatedDisplay !== homeScreen) {
          dispatch(slice1Actions.clearHTMLStack());
          dispatch(slice1Actions.setHomeScreen({ value: updatedDisplay }));
        }
      }
    }
  };

  const toggleFormat = () => {
    const gs = JSON.parse(JSON.stringify(GuideSettings));
    const opts = { ...user_info, showHiddenPlaces: ShowHiddenPlaces };
    let html;

    if (GuideFormat === "Photo Mosaic") {
      // Switching TO User Content — use document only, never generate dynamically
      gs.mode = "custom_view";
      gs.itinerary.directions = true;
      gs.itinerary.info = true;
      gs.itinerary.photo = true;
      gs.itinerary.place_menu = true;
      html = window.guide_obj.document || "";
    } else {
      // Switching TO Photo Mosaic — use preserved original settings, never overwritten copy
      gs.mode = "edit";
      const mosaicIt = (window.guide_obj && window.guide_obj._originalItinerary)
        ? window.guide_obj._originalItinerary
        : MOSAIC_ITINERARY;
      gs.itinerary = { ...mosaicIt };
      html = window.guide_obj.createHTMLItinerary("Main", gs.itinerary, opts);
    }

    dispatch(slice1Actions.setGuideSettings({ value: gs }));
    dispatch(slice1Actions.clearHTMLStack());
    dispatch(slice1Actions.setHomeScreen({ value: html }));
  };

  const guideName =
    guide_meta && guide_meta.guide_info && guide_meta.guide_info.guide_name
      ? guide_meta.guide_info.guide_name
      : "Untitled Guide";

  const isSubPage = HTMLStack.length > 1;

  if (isSubPage) return null;

  return (
    <>
      <div className="gnb-bar">
        <button className="gnb-name" onClick={EditGuideInfo} title="Edit guide info">
          {guideName}
        </button>
        <button className="gnb-edit-btn" onClick={openEditMenu} title="Edit itinerary or guide info">
          <FontAwesomeIcon icon={solid("pen")} />
          Edit
        </button>
        <button
          className={`gnb-mode-btn${isMosaic ? " gnb-mode-btn--mosaic" : ""}`}
          onClick={toggleFormat}
          title={isMosaic ? "Switch to User Content" : "Switch to Photo Mosaic"}
        >
          <FontAwesomeIcon icon={isMosaic ? solid("file-lines") : solid("images")} />
        </button>
      </div>
      {EditMenuPos && (
        <div className="gnb-menu-overlay" onClick={closeEditMenu}>
          <div className="gnb-menu" style={{ top: EditMenuPos.top, right: EditMenuPos.right }} onClick={(e) => e.stopPropagation()}>
            <span className="gnb-menu-label">Edit</span>
            <button className="gnb-menu-item" onClick={() => { closeEditMenu(); EditGuideInfo(); }}>
              <FontAwesomeIcon icon={solid("image")} />
              <span>
                <strong>Edit Guide Info</strong>
                <em>Name, description and cover image</em>
              </span>
            </button>
            <button
              className={`gnb-menu-item${isMosaic ? " gnb-menu-item--disabled" : ""}`}
              disabled={isMosaic}
              onClick={() => { closeEditMenu(); HTMLEdit(); }}
            >
              <FontAwesomeIcon icon={solid("code")} />
              <span>
                <strong>Edit Itinerary</strong>
                <em>{isMosaic ? mosaicReason : "The page's text and layout (HTML)"}</em>
              </span>
            </button>
            <button className="gnb-menu-item" onClick={() => { closeEditMenu(); openDialog("editNotes"); }}>
              <FontAwesomeIcon icon={solid("note-sticky")} />
              <span>
                <strong>Edit My Notes</strong>
                <em>Your own notes for this guide</em>
              </span>
            </button>
            <button className="gnb-menu-item" onClick={() => { closeEditMenu(); openDialog("guideSettings"); }}>
              <FontAwesomeIcon icon={solid("gear")} />
              <span>
                <strong>Guide Settings</strong>
                <em>Schedule pane, itinerary format and links</em>
              </span>
            </button>
            {GuideEditMode && <span className="gnb-menu-label">Build</span>}
            {GuideEditMode && (
              <button className="gnb-menu-item" onClick={() => { closeEditMenu(); openDialog("buildMyTrip"); }}>
                <FontAwesomeIcon icon={solid("wand-magic-sparkles")} />
                <span>
                  <strong>Build My Trip</strong>
                  <em>Find places and build a schedule for you</em>
                </span>
              </button>
            )}
            {GuideEditMode && (
              <button
                className={`gnb-menu-item${itineraryDisabledReason ? " gnb-menu-item--disabled" : ""}`}
                disabled={!!itineraryDisabledReason}
                onClick={() => { closeEditMenu(); openCopyItinerary(); }}
              >
                <FontAwesomeIcon icon={solid("calendar-check")} />
                <span>
                  <strong>{hasItinerary ? "Update Itinerary from Schedule" : "Create Itinerary from Schedule"}</strong>
                  <em>
                    {itineraryDisabledReason ||
                      (hasItinerary ? "Rebuild the page from your schedule (replaces its current content)" : "Build the page from your schedule")}
                  </em>
                </span>
              </button>
            )}
            {GuideEditMode && <span className="gnb-menu-label">Guide</span>}
            {GuideEditMode && (
              <button className="gnb-menu-item" onClick={() => { closeEditMenu(); openDialog("clearGuide"); }}>
                <FontAwesomeIcon icon={solid("eraser")} />
                <span>
                  <strong>Clear Local Guide Content</strong>
                  <em>Remove the places and events from this copy (asks first)</em>
                </span>
              </button>
            )}
            {GuideEditMode && (
              <button
                className={`gnb-menu-item${reloadDisabledReason ? " gnb-menu-item--disabled" : ""}`}
                disabled={!!reloadDisabledReason}
                onClick={() => { closeEditMenu(); openDialog("reloadGuide"); }}
              >
                <FontAwesomeIcon icon={solid("cloud-arrow-down")} />
                <span>
                  <strong>Reload Guide from Cloud</strong>
                  <em>{reloadDisabledReason || "Discard unsaved changes and reload the saved guide (asks first)"}</em>
                </span>
              </button>
            )}
          </div>
        </div>
      )}
      {ShowHTMLEditorModal && (
        <HTMLEditorModal
          show={ShowHTMLEditorModal}
          onHide={() => setShowHTMLEditorModal(false)}
          html={HTMLEditorModalContent}
          callback={HTMLEditCallback}
          callback_rt={null}
          title={"Home Page"}
        />
      )}
      {GuideEditModalShow && (
        <GuideEditModal
          show={GuideEditModalShow}
          onHide={() => setGuideEditModalShow(false)}
          guide_meta={guide_meta}
          guide_info={guide_meta.guide_info}
          guide_info_id={guide_meta.guide_info_id}
          user_info={user_info}
          mode={GuideEditModalMode}
          callback={GuideEditModalCallback}
          folder={imageFolder}
          setImageFolder={setImageFolder}
        />
      )}
    </>
  );
};

export default GuideNameBar;
